/**
 * Meeting lifecycle state machine.
 *
 * States: idle -> lobby -> connecting -> live -> ended -> idle
 * Failure path: connecting -> ended
 */

import { createStore } from './store'
import type { MeetingPhase, MeetingState } from '../types'

/** Valid transitions: from -> set of allowed targets */
const VALID_TRANSITIONS: Record<MeetingPhase, readonly MeetingPhase[]> = {
  idle: ['lobby'],
  lobby: ['connecting', 'idle'],
  connecting: ['live', 'ended'],
  live: ['ended'],
  ended: ['idle'],
}

function canTransition(from: MeetingPhase, to: MeetingPhase): boolean {
  return VALID_TRANSITIONS[from].includes(to)
}

const INITIAL_STATE: MeetingState = {
  phase: 'idle',
  meetingCode: null,
  localPeerId: null,
  displayName: '',
  error: null,
}

export const meetingStore = createStore<MeetingState>(INITIAL_STATE)

function transition(to: MeetingPhase, patch?: Partial<MeetingState>): void {
  const current = meetingStore.getState().phase
  if (!canTransition(current, to)) {
    console.warn(
      `[meeting] invalid transition: ${current} -> ${to}`,
    )
    return
  }
  meetingStore.setState({ ...patch, phase: to })
}

/** Create a new meeting: idle -> lobby. Returns the meeting code. */
export function createMeeting(meetingCode: string): void {
  transition('lobby', {
    meetingCode,
    error: null,
  })
}

/** Join an existing meeting with a code: idle -> lobby */
export function joinMeeting(code: string): void {
  transition('lobby', {
    meetingCode: code,
    error: null,
  })
}

/** Start the call: lobby -> connecting */
export function startCall(): void {
  transition('connecting', { error: null })
}

/** Mark the call as live: connecting -> live */
export function callConnected(): void {
  transition('live', { error: null })
}

/** End the call: live -> ended (or connecting -> ended on failure) */
export function endCall(error?: string): void {
  transition('ended', { error: error ?? null })
}

/** Reset to idle: ended -> idle */
export function resetMeeting(): void {
  transition('idle', {
    meetingCode: null,
    localPeerId: null,
    displayName: '',
    error: null,
  })
}

/** Update display name without changing phase */
export function setDisplayName(name: string): void {
  meetingStore.setState({ displayName: name })
}

/** Set local peer ID without changing phase */
export function setLocalPeerId(peerId: string): void {
  meetingStore.setState({ localPeerId: peerId })
}

/** Cancel from lobby back to idle: lobby -> idle */
export function cancelLobby(): void {
  transition('idle', {
    meetingCode: null,
    error: null,
  })
}

/** Get current meeting phase */
export function getPhase(): MeetingPhase {
  return meetingStore.getState().phase
}
